import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import LocationPicker from './LocationPicker';
import SourceSearch, { forgetSourceSearches } from './SourceSearch';
import { forgetSearches, sourceError } from './TmdbSearch';
import { useLocations } from '../locations';
import { isbn10 } from '../fieldTypes';
import { presetSources, selectLabel, uiLang, useKinds } from '../kinds';
import { attributionOf, sourceLabel, useSources } from '../sources';
import { Alert, Button, Dialog, SegmentedControl } from '../ui';
import { BookIcon, GameIcon, WarningIcon } from '../ui/icons';
import { CopyField, copyFieldsOf } from '../scan/CopyFields';

/**
 * Adding a book (a type of a model with several sources, phase 4 §3.4): the
 * canvas's four ways — *Search* (SourceSearch: a title and/or an author, or
 * an ISBN looked up), *Scan* (the scanner page, phase 4 §5), *Photo*
 * (the existing photo import) and *Enter by hand* (the form). A result
 * picked shows its details and its source; the copy's own fields (format,
 * edition, place) are set before *Add*. The server fills the item through
 * the type's integration, completing the source's answer with the type's
 * other sources by ISBN (Open Library's cover for a BnF record).
 *
 * Video games (phase 4 §4b.9) use it too: the search narrowed by a platform
 * picker, the copy's fields platform first (platform, region, completeness,
 * media, edition, condition), and the same game owned on another console
 * said apart ("also on PS4") instead of the duplicate warning.
 */
function AddBookModal({ inventory, itemType, onAdded, onManual, onPhoto, onScan, scanLabel, onClose }) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const kinds = useKinds();
  const sources = useSources();
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [copy, setCopy] = useState({});
  const [locationId, setLocationId] = useState(null);
  const [adding, setAdding] = useState(false);

  const hasLocations = !!inventory.location_type_id;
  const { places } = useLocations(inventory.location_type_id);
  const game = itemType.preset === 'videogame';
  const copyFields = useMemo(() => copyFieldsOf(itemType), [itemType]);
  const platformField = copyFields.find(field => field.key === 'platform') || null;
  const typeSources = presetSources(itemType.preset, kinds).map(id => sourceLabel(id, sources, lang));
  const setCopyValue = (key, value) => setCopy(current => ({ ...current, [key]: value }));

  const select = (result) => {
    setSelected(result);
    setError(null);
    // A game on one console only: that is the copy's, unless the member chose one.
    const platforms = (result && result.extra && result.extra.platforms) || [];
    if (game && result && !copy.platform && platforms.length === 1) setCopyValue('platform', platforms[0]);
  };

  const add = async () => {
    if (!selected) return;
    setAdding(true);
    setError(null);
    const values = {};
    copyFields.forEach((field) => {
      const value = copy[field.key];
      if (value !== undefined && value !== null && String(value).trim() !== '') values[field.key] = typeof value === 'string' ? value.trim() : value;
    });
    try {
      const response = await api.post(`/inventories/${inventory.id}/items/from-source`, {
        source: selected.source,
        source_id: selected.source_id,
        item_type_id: itemType.id,
        lang,
        values,
        ...(hasLocations ? { location_id: locationId } : {}),
      });
      forgetSourceSearches();   // what is owned has just changed
      forgetSearches();
      onAdded(response.data);
    } catch (err) {
      const code = err.response?.data?.code;
      setError(code === 'invalid_value' && err.response?.data?.field === 'location'
        ? t('locations.errors.invalid_place')
        : sourceError(t, err, itemType.fields || []));
      setAdding(false);
    }
  };

  // A game's owned copies on another console are not duplicates (§4b.7): said apart, no "Add anyway".
  const allOwned = selected?.owned || [];
  const onOtherPlatform = (item) => game && !!copy.platform && !!item.platform && item.platform !== copy.platform;
  const owned = allOwned.filter(item => !onOtherPlatform(item));
  const alsoOn = [...new Set(allOwned.filter(onOtherPlatform).map(item => item.platform))];
  const isbn = selected?.identifiers?.isbn;
  const credit = selected ? attributionOf(selected.source, sources, lang) : null;
  const extra = selected?.extra || {};
  const series = [extra.series, extra.series_number].filter(Boolean).join(' — ');

  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title={t(game ? 'addGame.title' : 'addBook.title')}
      description={t('addFromSource.into', { name: inventory.name, type: itemType.name })}
      className="add-source"
      footer={(
        <>
          <p className="add-source__attribution">{t('addBook.attribution', { sources: typeSources.join(', ') })}</p>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={add} disabled={!selected} loading={adding} icon={owned.length > 0 ? WarningIcon : undefined}>
            {owned.length > 0 ? t('addFromSource.addAnyway') : t('addFromSource.add')}
          </Button>
        </>
      )}
    >
      <SegmentedControl
        label={t('addFromSource.how')}
        value="search"
        onChange={(id) => {
          if (id === 'manual') onManual();
          if (id === 'photo' && onPhoto) onPhoto();
          if (id === 'scan' && onScan) onScan();
        }}
        options={[
          { id: 'search', label: t('addFromSource.searchTab') },
          ...(onScan ? [{ id: 'scan', label: scanLabel || t('addBook.scanTab') }] : []),
          ...(onPhoto ? [{ id: 'photo', label: t('addBook.photoTab') }] : []),
          { id: 'manual', label: t('addBook.manualTab') },
        ]}
        className="add-source__how"
      />

      {error && <Alert tone="danger">{error}</Alert>}

      <div className={`add-source__layout${selected ? ' has-selection' : ''}`}>
        <SourceSearch itemType={itemType} lang={lang} selected={selected} onSelect={select} onManual={onManual}
                      {...(game ? { platform: copy.platform || '', onPlatform: (code) => setCopyValue('platform', code) } : {})} />

        {selected ? (
          <section aria-label={t('addFromSource.preview')} className="add-source__preview">
            <div className="add-source__summary">
              {selected.thumb
                ? <img src={selected.thumb} alt="" className="add-source__poster" />
                : <span className="add-source__poster source-poster--empty" aria-hidden="true" />}
              <div>
                <h3 className="add-source__title">{selected.title}</h3>
                {selected.subtitle && <p className="add-source__overview">{selected.subtitle}</p>}
                {selected.creators && selected.creators.length > 0 && <p>{selected.creators.join(', ')}</p>}
                <p className="add-source__overview">{[selected.year, selected.publisher, extra.imprint].filter(Boolean).join(' · ')}</p>
                {series && <p className="add-source__overview">{t('addBook.series', { series })}</p>}
                {isbn && (
                  <p className="add-source__overview">
                    ISBN {isbn}{isbn10(isbn) ? ` · ISBN-10 ${isbn10(isbn)}` : ''}
                  </p>
                )}
                {credit && (
                  <p className="add-source__credit">
                    <a href={credit.url} target="_blank" rel="noopener noreferrer">{credit.text}</a>
                  </p>
                )}
              </div>
            </div>

            {alsoOn.length > 0 && platformField && (
              <Alert tone="info" className="add-source__also">
                {t('addGame.alsoOn', { platforms: alsoOn.map(code => selectLabel(platformField, code, lang)).join(', ') })}
              </Alert>
            )}
            {selected.other_platform && (
              <p className="ui-field__hint">{t('sourceSearch.otherPlatform', { source: sourceLabel(selected.source, sources, lang) })}</p>
            )}
            {owned.length > 0 && (
              <Alert tone="warning" title={t('addFromSource.duplicateTitle')}>
                <ul className="add-source__owned">
                  {owned.map(item => (
                    <li key={item.item_id}>
                      <Link to={`/c/${item.inventory_id}/items/${item.item_id}`}>{item.title || t('addFromSource.untitled')}</Link>
                      {' — '}{item.inventory_name}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            <div className="add-source__fields">
              {copyFields.map(field => (
                <CopyField key={field.key} field={field} lang={lang} id={`source-copy-${field.key}`} value={copy[field.key]}
                           onChange={(value) => setCopyValue(field.key, value)} />
              ))}
            </div>
            {hasLocations && (
              <div className="item-field">
                <label htmlFor="source-location" className="ui-field__label">{t('collection.location')}</label>
                <LocationPicker inputId="source-location" places={places} value={locationId} onChange={setLocationId} />
              </div>
            )}
            <p className="ui-field__hint">{t(game ? 'addGame.filledFrom' : 'addBook.filledFrom', { source: sourceLabel(selected.source, sources, lang) })}</p>
          </section>
        ) : (
          <div className="add-source__hint" aria-hidden="true">
            {game ? <GameIcon size={28} strokeWidth={1.6} /> : <BookIcon size={28} strokeWidth={1.6} />}
            <p>{t(game ? 'addGame.pickHint' : 'addBook.pickHint')}</p>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default AddBookModal;
